// Z91: country helpers on top of the single ISO 3166-1 alpha-2 list in src/lib/cds/countries.ts
// (mirrors the `countries` table; generated by scripts/gen_countries.py).
import { ISO_COUNTRIES, isValidIsoCountry, countryNameToIso2 } from './cds/countries.ts'

export interface CountryOption {
  id: string
  code: string
  label: string // "GB — United Kingdom": searchable by code or by name
}

export const COUNTRIES: CountryOption[] = ISO_COUNTRIES.map(c => ({ id: c.code, code: c.code, label: `${c.code} — ${c.name}` }))

export function countryLabelForCode(code?: string | null): string {
  if (!code) return ''
  const found = ISO_COUNTRIES.find(c => c.code === code.toUpperCase())
  return found ? `${found.code} — ${found.name}` : code
}

const UK_POSTCODE_RE = /\b([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})\b/i
const UK_ALIASES = new Set(['UK', 'U.K.', 'ENGLAND', 'SCOTLAND', 'WALES', 'NORTHERN IRELAND', 'GREAT BRITAIN', 'UNITED KINGDOM'])

export interface ResolvedCountry {
  code: string | null // ISO-2, or null when it can't be determined safely
  postcode: string | null // a UK postcode that was embedded in the text ("M1 4AB GB"), to be saved separately
}

// Free text -> ISO-2 only when unambiguous. Handles "GB", "England", "UK", "China (CN)",
// and trailing-code strings such as "M1 4AB GB" (-> GB + postcode "M1 4AB"). Never guesses:
// anything else returns code=null so the caller leaves the field for a human.
export function resolveCountryText(raw?: string | null): ResolvedCountry {
  const text = (raw ?? '').trim()
  if (!text) return { code: null, postcode: null }
  const upper = text.toUpperCase()
  if (UK_ALIASES.has(upper)) return { code: 'GB', postcode: null }
  const direct = countryNameToIso2(text)
  if (direct) return { code: direct, postcode: null }
  const paren = upper.match(/\(([A-Z]{2})\)\s*$/)
  if (paren && isValidIsoCountry(paren[1])) return { code: paren[1], postcode: null }
  const pc = upper.match(UK_POSTCODE_RE)
  const postcode = pc ? `${pc[1]} ${pc[2]}` : null
  const tokens = upper.replace(/,/g, ' ').split(/\s+/).filter(Boolean)
  const last = tokens[tokens.length - 1]
  if (last && /^[A-Z]{2}$/.test(last) && isValidIsoCountry(last)) {
    // a UK-format postcode with a non-GB code is contradictory -> don't guess
    if (postcode && last !== 'GB') return { code: null, postcode: null }
    return { code: last, postcode }
  }
  return { code: null, postcode: null }
}
